import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Card {
  id: number;
  name: string;
  imageUrl?: string;
  imageBlobPath?: string; // Legacy/Local
  rarity?: string;
  type?: string;
  cardNumber?: string;
  setName?: string;
  language?: string; // e.g. 'TH', 'JP', 'EN', 'ZH'
}

export interface User {
  id: number;
  username: string;
  displayName: string;
  email: string;
  role: 'user' | 'admin' | 'moderator' | 'approved_user' | 'advanced_user';
  profilePicture?: string;
  isBanned: boolean;
  isMuted: boolean;
  // Added properties
  points?: number;
  isBannedFromReporting?: boolean;
  firstName?: string;
  lastName?: string;
  isActive?: boolean;
}

export interface MarketplaceListing {
  id: number;
  seller: User;
  card?: Card;
  price: number;
  type: 'WTS' | 'WTB';
  status: 'ACTIVE' | 'SOLD' | 'CANCELLED';
  description?: string;
  imageUrls?: string;
  createdAt: string;
  shippingCost?: number;
  quantity: number;
}

export interface Auction {
  id: number;
  title?: string;
  description?: string;
  facebookLink: string;
  publicToken: string;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  startPrice: number;
  currentPrice: number;
  minIncrement: number;
  startTime: string;
  endTime: string;
  imageUrls: string;
  seller: User;
  card?: Card; // Legacy/Fallback
  items?: { id: number, card: Card }[]; // New Multi-card support
  winner?: User;
  bids?: {
    id: number;
    amount: number;
    bidder: User;
    createdAt: string;
  }[];
}

export interface Announcement {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  createdAt: string;
}

export interface SupportTicket {
  id: number;
  userId: number;
  subject: string;
  message: string;
  status: 'open' | 'pending' | 'resolved' | 'closed' | 'REJECTED' | 'RESOLVED';
  createdAt: string;
  user?: User;
  imagePath?: string;
}

export interface CarouselSlide {
  id: number;
  image: string;
  imageUrl?: string;
  imageAlt: string;
  label: string;
  heading: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  gradient: string;
  order: number;
  displayOrder?: number; // Alias for order
  isActive: boolean;
}

export interface CompetitionSchedule {
  id: number;
  title: string;
  date: string | Date;
  location: string;
  description: string;
  image?: string;
  isActive?: boolean;
}

export interface PriceStats {
  average: number;
  min: number;
  max: number;
  volume: number;
  // Extended fields for CardDetailPage
  hasData?: boolean;
  avgPrice?: number;
  minPrice?: number;
  maxPrice?: number;
  confidence?: 'high' | 'medium' | 'low';
  totalSubmissions?: number;
  recentSubmissions?: number;
}

export interface PriceHistoryPoint {
  date: string;
  price: number;
  avgPrice?: number;
  lowPrice?: number;
  highPrice?: number;
}

export interface MarketplaceListingReport {
  id: number;
  listingId: number;
  reporterId: number;
  reason: string;
  status: 'OPEN' | 'RESOLVED' | 'REJECTED';
  createdAt: string;
  listing?: MarketplaceListing;
  reporter?: User;
}

export interface LeaderboardUser {
  id: number;
  displayName: string;
  profilePicture?: string;
  points: number;
  rank: number;
  approvedSubmissions: number;
}

export interface PriceSubmission {
  id: number;
  card: Card;
  cardId: number; // Added
  submittedPrice: number;
  submittedAt: string;
  submitter?: User;
  source: string;
  sourceUrl?: string; // and in usage seems optional
  priceDate: string;
  proofImageUrl?: string;
}

export const authService = {
  login: (data: any) => api.post('/auth/login', data),
  register: (data: any) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  checkAuth: () => api.get('/auth/check').then((res) => res.data?.authenticated),
  getProfile: () => api.get('/auth/me').then((res) => res.data?.data),
  checkDiscordMembership: () => api.get('/auth/discord/membership').then((res) => res.data),
  updateProfile: (data: FormData) => api.put('/users/profile', data),
  loginWithDiscord: () => { window.location.href = `${API_URL}/auth/discord`; },
  getLineLoginUrl: (redirectUri: string) => api.get('/auth/line/url', { params: { redirectUri } }).then(res => res.data.url),
  linkLineAccount: (code: string, redirectUri: string) => api.post('/auth/line/callback', { code, redirectUri }),
  loginWithLine: () => { /* Handled by component or direct URL */ },
  loginWithFacebook: () => { window.location.href = `${API_URL}/auth/facebook`; },
  loginWithGoogle: () => { window.location.href = `${API_URL}/auth/google`; },
  logout: () => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/login'; },
};

export const cardService = {
  getCards: (params?: any) => api.get('/cards', { params }),
  getCard: (id: string) => api.get(`/cards/${id}`),
  createCard: (data: FormData) => api.post('/cards', data),
  updateCard: (id: string, data: FormData) => api.put(`/cards/${id}`, data),
  deleteCard: (id: string) => api.delete(`/cards/${id}`),
  getTopWatchlisted: () => api.get('/cards/stats/top-watchlisted'),
  getTopMarketplaceActivity: () => api.get('/cards/stats/top-marketplace-activity'),
};

export const marketplaceService = {
  getListings: (params?: any) => api.get('/marketplace', { params }),
  getListing: (id: string) => api.get(`/marketplace/${id}`),
  createListing: (data: any) => api.post('/marketplace', data), // Accepts JSON or FormData? Controller uses req.body/req.files, so likely FormData if images
  updateListing: (id: string, data: any) => api.put(`/marketplace/${id}`, data),
  deleteListing: (id: string) => api.delete(`/marketplace/${id}`),
  reportListing: (id: string, data: any) => api.post(`/marketplace/${id}/report`, data),
  closeListing: (id: string, price: number) => api.post(`/marketplace/${id}/close`, { price }), // Admin close
};

export const auctionService = {
  getAuctions: (params?: any) => api.get('/auctions', { params }),
  getAuction: (id: string) => api.get(`/auctions/${id}`),
  createAuction: (data: FormData) => api.post('/auctions', data),
  placeBid: (id: string, amount: number) => api.post(`/auctions/${id}/bids`, { amount }),
  getBids: (id: string, params?: any) => api.get(`/auctions/${id}/bids`, { params }),
  cancelAuction: (id: string) => api.post(`/auctions/${id}/cancel`),
  getAuctionById: (id: string) => api.get(`/auctions/${id}`),
  getBatchAuctions: (batchId: string) => api.get(`/auctions/batch/${batchId}`),
  getActiveBatches: (limit: number = 6) => api.get(`/auctions/batches`, { params: { limit } }),
  cancelBatch: (batchId: string) => api.post(`/auctions/batch/${batchId}/cancel`),
};

export const transactionService = {
  createTransaction: (listingId: number) => api.post('/transactions', { listingId }),
  getTransaction: (id: string) => api.get(`/transactions/${id}`),
  getUserTransactions: (role: 'buyer' | 'seller') => {
    if (role === 'buyer') return api.get('/transactions/purchases');
    return api.get('/transactions/sales');
  },
  getUserPurchases: (params?: any) => api.get('/transactions/purchases', { params }),
  getUserSales: (params?: any) => api.get('/transactions/sales', { params }),
  uploadPaymentSlip: (id: string, formData: FormData) => api.post(`/transactions/${id}/upload-payment`, formData),
  confirmPayment: (id: string) => api.post(`/transactions/${id}/confirm-payment`),
  uploadShippingProof: (id: string, formData: FormData) => api.post(`/transactions/${id}/upload-shipping`, formData),
  confirmReceipt: (id: string) => api.post(`/transactions/${id}/confirm-receipt`),
  cancelTransaction: (id: string, reason: string) => api.post(`/transactions/${id}/cancel`, { reason }),
};

export const reviewService = {
  createReview: (transactionId: string, data: { rating: number, comment?: string }) => api.post(`/reviews/transaction/${transactionId}`, data),
  getUserReviews: (userId: string, params?: any) => api.get(`/reviews/user/${userId}`, { params }),
};

export const userService = {
  getUsers: (params?: any) => api.get('/users', { params }),
  getUser: (id: string | number) => api.get(`/users/${id}`),
  updateUserRole: (id: string | number, role: string) => api.patch(`/users/${id}/role`, { role }),
  updateUserStatus: (id: string | number, isBanned: boolean, isMuted: boolean) => api.patch(`/users/${id}/status`, { isBanned, isMuted }),
  updateUserDetails: (id: string | number, data: any) => api.patch(`/users/${id}/details`, data),
  deleteUser: (id: string | number) => api.delete(`/users/${id}`),
};


export const announcementService = {
  getActiveAnnouncements: () => api.get('/announcements/active'),
  getAnnouncements: (params?: any) => api.get('/announcements', { params }), // Admin
  createAnnouncement: (data: any) => api.post('/announcements', data),
  updateAnnouncement: (id: number, data: any) => api.put(`/announcements/${id}`, data),
  deleteAnnouncement: (id: number) => api.delete(`/announcements/${id}`),
};

export const carouselSlideService = {
  getCarouselSlides: () => api.get('/carousel-slides'),
  createCarouselSlide: (data: FormData) => api.post('/carousel-slides', data),
  updateCarouselSlide: (id: number, data: FormData) => api.put(`/carousel-slides/${id}`, data),
  deleteCarouselSlide: (id: number) => api.delete(`/carousel-slides/${id}`),
  reorderSlides: (orders: any) => api.patch('/carousel-slides/reorder', { orders }),
  getAdminCarouselSlides: () => api.get('/carousel-slides'),
};

export const homeService = {
  getMostActiveCards: (limit: number) => api.get('/cards/most-active', { params: { limit } }),
  getTopWatchlisted: (limit: number) => api.get('/cards/top-watchlisted', { params: { limit } }),
  getTopMarketplaceActivity: (limit: number) => api.get('/cards/top-marketplace', { params: { limit } }),
};

export const priceSubmissionService = {
  getLatestPriceUpdates: (limit: number) => api.get('/price-submissions/latest', { params: { limit } }),
  getPriceSubmissions: (params?: any) => api.get('/price-submissions', { params }), // Admin
  moderateSubmission: (id: number, data: { status: 'approved' | 'rejected' }) => api.patch(`/price-submissions/${id}/moderate`, data),
  adminUpdatePriceSubmission: (id: number, data: any) => api.patch(`/price-submissions/admin/${id}`, data),
  adminDeletePriceSubmission: (id: number) => api.delete(`/price-submissions/admin/${id}`),
  getLeaderboard: (limit: number) => api.get('/price-submissions/leaderboard', { params: { limit } }),
  getCardPriceStats: (cardId: string) => api.get(`/price-submissions/cards/${cardId}/price-stats`),
  getBatchCardPriceStats: (cardIds: number[]) => api.post('/price-submissions/batch-stats', { cardIds }),
  getCardPriceHistory: (cardId: string, days: number) => api.get(`/price-submissions/cards/${cardId}/price-history`, { params: { days } }),
  submitPrice: (data: FormData) => api.post('/price-submissions', data),
};

export const marketplaceListingService = {
  getRecentListings: (limit: number) => api.get('/marketplace-listings/recent', { params: { limit } }),
  getListings: (params?: any) => api.get('/marketplace-listings', { params }),
  getListingById: (id: string) => api.get(`/marketplace-listings/${id}`),
  getListingChanges: (id: string) => api.get(`/marketplace-listings/${id}/changes`),
  createListing: (data: FormData) => api.post('/marketplace-listings', data),
  updateListing: (id: string, data: FormData) => api.put(`/marketplace-listings/${id}`, data),
  cancelListing: (id: string, reason?: string) => api.post(`/marketplace-listings/${id}/cancel`, { reason }),
  closeListing: (id: string, data: any) => api.post(`/marketplace-listings/${id}/close`, data),
  getUserListings: (userId: string, params?: any) => api.get(`/marketplace-listings/user/${userId}`, { params }),
  getAdminListings: (params?: any) => api.get('/marketplace-listings/admin', { params }),
};

export const marketplaceListingReportService = {
  createReport: (listingId: string | number, data: { reason: string }) => api.post(`/marketplace-listing-reports/${listingId}`, data),
  getReports: (params?: any) => api.get('/marketplace-listing-reports', { params }), // Admin
  resolveReport: (id: string | number, data: { closedPrice?: number }) => api.post(`/marketplace-listing-reports/${id}/resolve`, data),
  rejectReport: (id: string | number) => api.post(`/marketplace-listing-reports/${id}/reject`),
};

export const competitionScheduleService = {
  getSchedules: (params?: any) => api.get('/competition-schedules', { params }),
  getAdminSchedules: (params?: any) => api.get('/competition-schedules/admin', { params }),
  createSchedule: (data: FormData) => api.post('/competition-schedules', data),
  updateSchedule: (id: string | number, data: FormData) => api.patch(`/competition-schedules/${id}`, data),
  deleteSchedule: (id: string | number) => api.delete(`/competition-schedules/${id}`),
};

export const messageService = {
  sendMessage: (data: any) => api.post('/messages', data),
  getConversations: (params?: any) => api.get('/messages/conversations', { params }),
  getTransactionMessages: (transactionId: string) => api.get(`/messages/transaction/${transactionId}`),
  getConversationMessages: (userId: string, params?: any) => api.get(`/messages/conversation/${userId}`, { params }),
  markAsRead: (id: string) => api.post(`/messages/${id}/read`),
};

export const notificationService = {
  getNotifications: (params?: any) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id: string | number) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
};

export const watchlistService = {
  getWatchlistStatus: (cardId: string) => api.get(`/watchlist/status/${cardId}`),
  getWatchlist: () => api.get('/watchlist'),
  addToWatchlist: (cardId: string | number | { cardId: number }) => {
    const id = typeof cardId === 'object' ? cardId.cardId : cardId;
    return api.post('/watchlist', { cardId: id });
  },
  removeFromWatchlist: (cardId: string) => api.delete(`/watchlist/${cardId}`),
};

export const supportService = {
  createTicket: (data: FormData) => api.post('/support', data),
  getTickets: (params?: any) => api.get('/support/admin', { params }),
  updateTicketStatus: (id: string | number, status: string) => api.patch(`/support/${id}/status`, { status }),
};

export const listingService = marketplaceListingService;

export default api;
